import * as i0 from '@angular/core';
import { Injectable, InjectionToken, inject, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
let BrowserLocalStorageService = /*#__PURE__*/(() => {
  class BrowserLocalStorageService {
    setItem(key, value) {
      localStorage.setItem(key, value);
    }
    getItem(key) {
      return localStorage.getItem(key);
    }
    removeItem(key) {
      localStorage.removeItem(key);
    }
    clear() {
      localStorage.clear();
    }
    static ɵfac = function BrowserLocalStorageService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || BrowserLocalStorageService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: BrowserLocalStorageService,
      factory: BrowserLocalStorageService.ɵfac,
      providedIn: 'root'
    });
  }
  return BrowserLocalStorageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ServerLocalStorageService = /*#__PURE__*/(() => {
  class ServerLocalStorageService {
    storage = {};
    setItem(key, value) {
      this.storage[key] = value;
    }
    getItem(key) {
      return this.storage[key] || null;
    }
    removeItem(key) {
      delete this.storage[key];
    }
    clear() {
      this.storage = {};
    }
    static ɵfac = function ServerLocalStorageService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ServerLocalStorageService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ServerLocalStorageService,
      factory: ServerLocalStorageService.ɵfac,
      providedIn: 'root'
    });
  }
  return ServerLocalStorageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const F_LOCAL_STORAGE = new InjectionToken('F_LOCAL_STORAGE', {
  providedIn: 'root',
  factory: () => {
    return isPlatformBrowser(inject(PLATFORM_ID)) ? inject(BrowserLocalStorageService) : inject(ServerLocalStorageService);
  }
});
let BrowserWindowService = /*#__PURE__*/(() => {
  class BrowserWindowService {
    getComputedStyle(element) {
      return window.getComputedStyle(element);
    }
    get innerWidth() {
      return window.innerWidth;
    }
    get innerHeight() {
      return window.innerHeight;
    }
    get pageXOffset() {
      return window.pageXOffset;
    }
    get pageYOffset() {
      return window.pageYOffset;
    }
    isMediaQuery(query) {
      return window.matchMedia(query).matches;
    }
    requestAnimationFrame(callback) {
      return window.requestAnimationFrame(callback);
    }
    get location() {
      return window.location;
    }
    open(url, target, features) {
      window.open(url, target, features);
    }
    static ɵfac = function BrowserWindowService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || BrowserWindowService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: BrowserWindowService,
      factory: BrowserWindowService.ɵfac,
      providedIn: 'root'
    });
  }
  return BrowserWindowService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ServerWindowService = /*#__PURE__*/(() => {
  class ServerWindowService {
    getComputedStyle(element) {
      // @ts-ignore
      return new Proxy({
        getPropertyValue: property => {
          return '0';
        }
      }, {
        get: (target, prop) => {
          if (prop === 'getPropertyValue') {
            return target.getPropertyValue;
          }
          return '0';
        }
      });
    }
    get innerWidth() {
      return 0;
    }
    get innerHeight() {
      return 0;
    }
    get pageXOffset() {
      return 0;
    }
    get pageYOffset() {
      return 0;
    }
    isMediaQuery(query) {
      return false;
    }
    requestAnimationFrame(callback) {
      return setTimeout(callback, 0);
    }
    get location() {
      return {
        href: '',
        pathname: '',
        search: '',
        hash: '',
        origin: ''
      };
    }
    open(url, target, features) {
      // Do nothing
    }
    static ɵfac = function ServerWindowService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ServerWindowService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ServerWindowService,
      factory: ServerWindowService.ɵfac,
      providedIn: 'root'
    });
  }
  return ServerWindowService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const F_WINDOW = new InjectionToken('F_WINDOW', {
  providedIn: 'root',
  factory: () => {
    return isPlatformBrowser(inject(PLATFORM_ID)) ? inject(BrowserWindowService) : inject(ServerWindowService);
  }
});
var EOperationSystem = /*#__PURE__*/function (EOperationSystem) {
  EOperationSystem["MAC_OS"] = "macos";
  EOperationSystem["IOS"] = "ios";
  EOperationSystem["WINDOWS"] = "windows";
  EOperationSystem["ANDROID"] = "android";
  EOperationSystem["LINUX"] = "linux";
  return EOperationSystem;
}(EOperationSystem || {});
let hasV8BreakIterator;
try {
  hasV8BreakIterator = typeof Intl !== 'undefined' && Intl.v8BreakIterator;
} catch {
  hasV8BreakIterator = false;
}
let PlatformService = /*#__PURE__*/(() => {
  class PlatformService {
    _platformId;
    isBrowser = false;
    EDGE = this.isBrowser && /(edge)/i.test(navigator.userAgent);
    TRIDENT = this.isBrowser && /(msie|trident)/i.test(navigator.userAgent);
    BLINK = this.isBrowser && !!(window.chrome || hasV8BreakIterator) && typeof CSS !== 'undefined' && !this.EDGE && !this.TRIDENT;
    WEBKIT = this.isBrowser && /AppleWebKit/i.test(navigator.userAgent) && !this.BLINK && !this.EDGE && !this.TRIDENT;
    IOS = this.isBrowser && /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
    FIREFOX = this.isBrowser && /(firefox|minefield)/i.test(navigator.userAgent);
    ANDROID = this.isBrowser && /android/i.test(navigator.userAgent) && !this.TRIDENT;
    SAFARI = this.isBrowser && /safari/i.test(navigator.userAgent) && this.WEBKIT;
    constructor(_platformId) {
      this._platformId = _platformId;
      this.isBrowser = this._platformId ? isPlatformBrowser(this._platformId) : typeof document === 'object' && !!document;
    }
    getOS() {
      let userAgent = navigator.userAgent.toLowerCase(),
        macosPlatforms = /(macintosh|macintel|macppc|mac68k|macos)/i,
        windowsPlatforms = /(win32|win64|windows|wince)/i;
      let result;
      if (macosPlatforms.test(userAgent)) {
        result = EOperationSystem.MAC_OS;
      } else if (this.IOS) {
        result = EOperationSystem.IOS;
      } else if (windowsPlatforms.test(userAgent)) {
        result = EOperationSystem.WINDOWS;
      } else if (this.ANDROID) {
        result = EOperationSystem.ANDROID;
      } else if (!result && /linux/.test(userAgent)) {
        result = EOperationSystem.LINUX;
      }
      return result;
    }
    static ɵfac = function PlatformService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PlatformService)(i0.ɵɵinject(PLATFORM_ID));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PlatformService,
      factory: PlatformService.ɵfac,
      providedIn: 'root'
    });
  }
  return PlatformService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let BrowserService = /*#__PURE__*/(() => {
  class BrowserService {
    injectedDocument;
    windowService;
    localStorageService;
    platformService;
    constructor(injectedDocument, windowService, localStorageService, platformService) {
      this.injectedDocument = injectedDocument;
      this.windowService = windowService;
      this.localStorageService = localStorageService;
      this.platformService = platformService;
    }
    isBrowser() {
      return this.platformService.isBrowser;
    }
    get window() {
      return this.windowService;
    }
    get localStorage() {
      return this.localStorageService;
    }
    get document() {
      return this.injectedDocument;
    }
    toPixels(value, clientWidth, clientHeight, fontSize) {
      if (value.endsWith('px')) {
        return parseFloat(value);
      } else if (value.endsWith('%')) {
        const percentage = parseFloat(value) / 100;
        return Math.max(clientWidth, clientHeight) * percentage;
      } else if (value.endsWith('em')) {
        return parseFloat(value) * parseFloat(fontSize);
      } else if (value.endsWith('rem')) {
        return parseFloat(value) * parseFloat(getComputedStyle(this.document.documentElement).fontSize);
      } else if (value.endsWith('vh')) {
        const vh = this.window.innerHeight / 100;
        return parseFloat(value) * vh;
      } else if (value.endsWith('vw')) {
        const vw = this.window.innerWidth / 100;
        return parseFloat(value) * vw;
      }
      return parseFloat(value) || 0;
    }
    static ɵfac = function BrowserService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || BrowserService)(i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(F_WINDOW), i0.ɵɵinject(F_LOCAL_STORAGE), i0.ɵɵinject(PlatformService));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: BrowserService,
      factory: BrowserService.ɵfac,
      providedIn: 'root'
    });
  }
  return BrowserService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { BrowserService, EOperationSystem, F_LOCAL_STORAGE, F_WINDOW, PlatformService };
